import { useEffect, useState } from 'react'
import permissionsService, {
  Capability,
  CapabilityMap,
  MyPermissions,
  Role,
} from 'services/permissions'

// Capability-aware UI gating for product#618 (Phase B FE), driven entirely off
// the be#214 `GET /locations/<id>/my-permissions` payload — never a hardcoded
// role name. A PRO (or any role whose resolved set denies a capability) does not
// see / cannot trigger the gated control.
//
// The facility routes are mounted independently (no shared layout wrapper), so
// rather than a context provider this hook shares a single fetch per location
// via a module-level cache + in-flight dedupe: every gated component can call
// `useCapabilities(locationId)` and they all resolve from one request.

const cache = new Map<string, MyPermissions>()
const inflight = new Map<string, Promise<MyPermissions | null>>()

// Call on logout so a different user in the same tab does not inherit the prior
// user's resolved capabilities (the JS cache outlives redux-persist's clear).
export const clearCapabilitiesCache = (): void => {
  cache.clear()
  inflight.clear()
}

export type UseCapabilities = {
  role: Role | null
  capabilities: CapabilityMap | null
  loading: boolean
  // UX gate. Denies while the fetch is in flight (unresolved) so a restricted
  // control never flash-shows; returns false when a resolved payload denies the
  // capability; fails open (true) only for a RESOLVED null (endpoint absent /
  // network error) — the backend (be#214) is the authoritative default-deny.
  can: (capability: Capability) => boolean
}

export function useCapabilities(
  locationId?: string | number | null
): UseCapabilities {
  const key = locationId != null && locationId !== '' ? String(locationId) : ''
  const [perms, setPerms] = useState<MyPermissions | null>(
    () => cache.get(key) ?? null
  )
  const [loading, setLoading] = useState<boolean>(
    () => key !== '' && !cache.has(key)
  )

  useEffect(() => {
    if (!key) {
      setPerms(null)
      setLoading(false)
      return
    }

    const cached = cache.get(key)
    if (cached) {
      setPerms(cached)
      setLoading(false)
      return
    }

    let active = true
    setLoading(true)

    let req = inflight.get(key)
    if (!req) {
      req = permissionsService.getMyPermissions(key).then(res => {
        if (res) cache.set(key, res)
        inflight.delete(key)
        return res
      })
      inflight.set(key, req)
    }

    req.then(res => {
      if (!active) return
      setPerms(res ?? null)
      setLoading(false)
    })

    return () => {
      active = false
    }
  }, [key])

  // Deny while the fetch is UNRESOLVED — prevents a restricted control from
  // flash-showing before my-permissions returns on a cold load. Fail-open only
  // for a RESOLVED null (endpoint absent / network error on an un-migrated
  // backend), the case the "don't lock staff out" rationale actually covers.
  const can = (capability: Capability): boolean =>
    loading && !perms
      ? false
      : perms?.capabilities
      ? perms.capabilities[capability] !== false
      : true

  return {
    role: perms?.role ?? null,
    capabilities: perms?.capabilities ?? null,
    loading,
    can,
  }
}
